import React, { useEffect, useRef } from "react";
import { useCart } from "../context/CartContext";
import { useNavigate, Link } from "react-router-dom";
import { getImageUrl } from "../utility/imageHelper";
import { slugify } from "../utility/slugify"; // ✅ Import slugify to create the URL
import './styles/Cart.css';
import SEO from "../seo/SEO";
import config from "../config/config";
import generateBreadcrumbs from "../utility/generateBreadcrumbs";
import { useStore } from "../context/StoreContext";


import { useAnalytics } from "../context/AnalyticsProvider";

const Cart = () => {
  const { cart, removeFromCart, updateQuantity } = useCart();
  const { products } = useStore();
  const navigate = useNavigate();
  const analytics = useAnalytics(); // ✅ Init analytics context
  const hasFiredViewCartRef = useRef(false);


  const handleCheckout = () => {
    navigate("/secure-checkout")
  }

  const totalPrice = cart.reduce((total, item) => total + item.price * item.quantity, 0);


  useEffect(() => {
    if (cart.length === 0) return;

    // Prevent double-firing in dev StrictMode
    if (hasFiredViewCartRef.current) return;

    analytics.track("view_cart", {
      currency: "USD",
      value: totalPrice,
      items: cart.map(item => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity,
        category: item.category_name,
        stock_remaining: item.stock - item.quantity,
      })),
    });

    hasFiredViewCartRef.current = true;
  }, [cart.length]);  // ✔ ONLY cart.length

  // Detect duplicate names globally
  const nameCounts = products.reduce((acc, p) => {
    acc[p.name] = (acc[p.name] || 0) + 1;
    return acc;
  }, {});

  const duplicateNames = new Set(
    Object.entries(nameCounts)
      .filter(([_, count]) => count > 1)
      .map(([name]) => name)
  );

  return (
    <>
        <SEO
            robots="noindex, follow"
            title="Shopping Cart - Withrow Knives"
            description="View your Withrow Knives shopping cart. Secure your custom handmade knives and custom leather sheaths before checkout."
            keywords={[
                "shopping cart",
                "custom knives",
                "handmade knives",
                "Withrow Knives cart",
                "knife checkout",
                "leather sheaths"
            ]}
            url={`${config.metadata.siteUrl}/shopping-cart`}
            jsonLd={{
                "@context": "https://schema.org",
                "@type": "ShoppingCart",
                "name": "Withrow Knives Shopping Cart",
                "url": `${config.metadata.siteUrl}/shopping-cart`,
                "description": "Your selected Withrow Knives products are ready for checkout. Secure your custom handmade knives and sheaths now.",
                "potentialAction": {
                    "@type": "BuyAction",
                    "target": `${config.metadata.siteUrl}/checkout`,
                    "name": "Proceed to Checkout"
                },
                "breadcrumb": generateBreadcrumbs(["shopping-cart"]),
                "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": `${config.metadata.siteUrl}/shopping-cart`
                }
            }}
        />


    
      <div className="cart-container">
        <h2>Shopping Cart</h2>

        {/* ✅ Cart Item Count */}
        {cart.length > 0 && (
          <p className="cart-item-count">
            {cart.reduce((total, item) => total + item.quantity, 0)} item
            {cart.reduce((total, item) => total + item.quantity, 0) > 1 ? "s" : ""}
          </p>
        )}

        {/* ✅ Empty Cart Message */}
        {cart.length === 0 ? (
          <p className="empty-cart">Your cart is empty.</p>
        ) : (
          <div className="cart-items">
            {cart.map((item) => (
              <div key={item.id} className="cart-item">
                {/* ✅ Image Link */}
                <Link to={`/shop-knives/knife/${item.id}/${slugify(item.name)}`}>
                  <img
                    src={getImageUrl(item.images?.[0] || item.image_url)}
                    alt={item.name}
                    className="cart-item-image"
                  />
                </Link>

                {/* ✅ Item Details */}
                <div className="cart-item-details">
                  <h3>
                    <Link
                      to={`/shop-knives/knife/${item.id}/${slugify(item.name)}`}
                      className="cart-item-link"
                    >
                      {item.name}
                      {duplicateNames.has(item.name) && (
                        <> <span className="cart-item-id">(#{item.id})</span></>  
                      )}
                    </Link>
                  </h3>
                  <p className="cart-item-price">${item.price} x {item.quantity}</p>

                  {/* ✅ Accessible Quantity Input */}
                  <div className="cart-item-quantity">
                    <label htmlFor={`cart-item-quantity-${item.id}`} className="sr-only">
                      Quantity for {item.name}
                    </label>
                    <input
                      id={`cart-item-quantity-${item.id}`}
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateQuantity(item.id, parseInt(e.target.value))}
                      min="1"
                      aria-label={`Change quantity for ${item.name}`}
                    />
                  </div>
                </div>

                {/* ✅ Accessible Remove Button */}
                <button
                  className="remove-btn"
                    onClick={() => {
                      analytics.track("remove_from_cart", {
                        currency: "USD",
                        value: item.price * item.quantity,
                        items: [{
                          item_id: item.id,
                          item_name: item.name,
                          price: item.price,
                          quantity: item.quantity,
                          category: item.category_name,
                          stock_remaining: item.stock - item.quantity,
                        }],
                      });
                      removeFromCart(item.id);
                    }}
                  aria-label={`Remove ${item.name} from cart`}
                >
                  <i className="fa-solid fa-trash-can"></i>
                </button>
              </div>
            ))}

            {/* ✅ Cart Summary & Checkout Button */}
            <div className="cart-summary">
              <h3>Subtotal: ${totalPrice.toFixed(2)}</h3>
              <button
                className="checkout-btn"
                onClick={handleCheckout}
                aria-label="Proceed to checkout"
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        )}
      </div>

    </>
  );
};

export default Cart;
